import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Product } from '~redux/brand/types';
import {
  clearedInventory,
  makeSelectAvailableInventoryBySku,
  makeSelectInventoriesByProduct,
  makeSelectInventoryByProduct,
  makeSelectInventoryQtyBySku,
  makeSelectIsOutOfStockByProduct,
  makeSelectIsOutOfStockByProductId,
  makeSelectIsOutOfStockBySku,
  selectIsInventoryLoading,
} from '~redux/inventory';
import { fetchInventories as fetchInventoriesThunk } from '~usecases/buyer/inventory';

export const useInventory = () => {
  const dispatch = useDispatch();
  const fetchInventories = useCallback((productIds: string[]) => {
    if (!productIds.length) return;
    dispatch(fetchInventoriesThunk(productIds));
  }, []);
  const clear = useCallback(() => {
    dispatch(clearedInventory());
  }, []);
  const isLoading = useSelector(selectIsInventoryLoading);
  return {
    fetchInventories,
    clear,
    isLoading,
  };
};

export const useSelectInventoriesByProduct = (productId: string) => {
  const selectInventories = useMemo(
    () => makeSelectInventoriesByProduct(productId),
    [productId]
  );
  return useSelector(selectInventories);
};

export const useSelectInventoryByProduct = (productId: string) => {
  const selectInventories = useMemo(
    () => makeSelectInventoryByProduct(productId),
    [productId]
  );
  return useSelector(selectInventories);
};

export const useSelectIsOutOfStockByProductId = (productId: string) => {
  const selector = useMemo(() => makeSelectIsOutOfStockByProductId(productId), [
    productId,
  ]);
  return useSelector(selector);
};

export const useSelectIsOutOfStockByProduct = (product: Product) => {
  const selector = useMemo(() => makeSelectIsOutOfStockByProduct(product), [
    product,
  ]);
  return useSelector(selector);
};

export const useSelectInventoryBySku = (
  brandId: string,
  sku?: string,
  productId?: string,
  productTypeId?: string
) => {
  const selector = useMemo(
    () =>
      makeSelectInventoryQtyBySku(brandId, sku ?? productTypeId ?? productId!),
    [productId]
  );
  return useSelector(selector);
};

export const useSelectIsOutOfStockBySku = (
  brandId: string,
  sku?: string,
  productId?: string,
  productTypeId?: string
) => {
  const selector = useMemo(
    () =>
      makeSelectIsOutOfStockBySku(brandId, sku ?? productTypeId ?? productId!),
    [productId]
  );
  return useSelector(selector);
};

export const useSelectAvailableInventoryBySku = (
  brandId: string,
  sku?: string,
  productId?: string,
  productTypeId?: string
) => {
  const selector = useMemo(
    () =>
      makeSelectAvailableInventoryBySku(
        brandId,
        sku ?? productTypeId ?? productId!
      ),
    [productId]
  );
  return useSelector(selector);
};
